import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Language, languages, t as translate } from '@/lib/translations';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'operota_lang';

const supportedCodes = new Set(languages.map((l) => l.code));

function detectBrowserLanguage(): Language {
  const raw = navigator.language || (navigator as unknown as { languages?: string[] }).languages?.[0] || 'en';
  const primary = raw.split('-')[0].toLowerCase();
  if (supportedCodes.has(primary as Language)) {
    return primary as Language;
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && supportedCodes.has(saved as Language)) {
      return saved as Language;
    }
    const detected = detectBrowserLanguage();
    localStorage.setItem(STORAGE_KEY, detected);
    return detected;
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>) => translate(language, key, vars),
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
